import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import {
  ChefHat, AlertTriangle, RefreshCcw, Loader2, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import ProductionRecommendationsPanel from "../components/production/ProductionRecommendationsPanel";
import api from "../lib/api";
import { PageHeader } from "../components/layout/PageHeader";

const fmtDate = (s) =>
  s
    ? new Date(s).toLocaleDateString("es-ES", {
        weekday: "short",
        day: "2-digit",
        month: "short",
      })
    : "—";

/**
 * Página de Producción:
 *  1. Panel de recomendaciones IA (consumo medio → elaboraciones base).
 *  2. Alertas de caducidad próxima.
 *  3. Histórico de cierre de producción (últimos 30 días).
 *
 * El flujo completo de cierre (con vincular platos, mermas, etc.) sigue
 * disponible desde el panel de Cierres (/app/cash).
 */
export default function ProductionPage() {
  const { currentRestaurant } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!currentRestaurant?.restaurant_id) return;
    const rid = currentRestaurant.restaurant_id;
    try {
      const [logsRes, expRes] = await Promise.all([
        api.request(`/restaurants/${rid}/production-logs?limit=30`),
        api
          .request(`/restaurants/${rid}/production-logs/expiring?days=3`)
          .catch(() => ({ items: [] })),
      ]);
      setLogs(logsRes?.logs || []);
      setExpiring(expRes?.items || []);
    } catch {
      toast.error("No se pudo cargar la producción");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentRestaurant?.restaurant_id]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const rid = currentRestaurant?.restaurant_id;

  return (
    <div className="space-y-6" data-testid="production-page">
      {/* ── Header ── */}
      <PageHeader
        icon={<ChefHat className="w-5 h-5" />}
        title="Producción"
        description="Elaboraciones base recomendadas por IA · Confirma lo producido · Histórico de cierres."
        breadcrumb={[{ label: "Carta y cocina" }, { label: "Producción" }]}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRefreshing(true);
              loadHistory();
            }}
            disabled={refreshing}
          >
            <RefreshCcw
              className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`}
            />
            Actualizar
          </Button>
        }
      />

      {/* ── AI Recommendations panel ── */}
      {rid && (
        <ProductionRecommendationsPanel
          restaurantId={rid}
          onProductionConfirmed={loadHistory}
        />
      )}

      {loading ? (
        <Card>
          <CardContent className="py-12 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── Expiring alert ── */}
          {expiring.length > 0 && (
            <Card className="border-amber-300 bg-amber-50/40 dark:bg-amber-950/20">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-700" />
                  <p className="font-semibold text-sm text-amber-900 dark:text-amber-100">
                    Caducidades próximas (3 días)
                  </p>
                  <Badge variant="secondary" className="ml-auto">
                    {expiring.length}
                  </Badge>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Cantidad</TableHead>
                      <TableHead>Caduca</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expiring.slice(0, 10).map((row, idx) => (
                      <TableRow
                        key={idx}
                        data-testid={`expiring-row-${idx}`}
                      >
                        <TableCell className="font-medium text-sm">
                          {row.item?.name || "—"}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {row.item?.qty} {row.item?.unit || ""}
                        </TableCell>
                        <TableCell className="text-sm">
                          {fmtDate(row.item?.expires_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* ── Production history ── */}
          <Card>
            <CardContent className="p-0">
              <div className="px-5 py-4 border-b flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-sm">Histórico de producción</p>
                  <p className="text-xs text-muted-foreground">Últimos 30 días</p>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />
                  <a
                    href="/app/cash"
                    className="underline underline-offset-2 hover:text-foreground transition-colors"
                  >
                    Cierre avanzado en Cierres
                  </a>
                </div>
              </div>
              {logs.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  Aún no has registrado producción. Usa el panel de arriba para cerrar el primer día.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Día</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead className="text-center">Mermas</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow
                        key={log.log_id || log.day}
                        data-testid={`production-row-${log.day}`}
                      >
                        <TableCell className="text-sm">{fmtDate(log.day)}</TableCell>
                        <TableCell className="text-center text-sm">
                          {log.items_count ?? "—"}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {log.waste_count ?? "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">
                            {log.status === "closed" ? "Cerrado" : "Confirmado"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
